import { EventEmitter, Injectable } from '@angular/core';
import { AuthService } from '@auth';
import { SecurityService } from './security.service';
import { environment } from 'src/environments/environment';
import { AppSettingsService } from './app-settings.service';
import { FirestoreService } from './firestore.service';
import { PullDataService } from './pull-data.service';
import { PushDataService } from './push-data.service';
import { Guid } from 'guid-typescript';
import { OnlineOfflineService, SystemSettingsService } from '@core';
import { ModuleActivationService } from './module-activation.service';
import { RecoveryService } from './replication/recovery.service';
import { CashTransactionService } from './cash-transaction.service';
import { CashRegisterStatusLogReplicationService } from './cash-register-status-log-replication.service';
import { SalesOrderV3ReplicationService } from './sales-order-v3-replication.service';
import { SalesOrderReplicationService } from './sales-order-replication.service';

@Injectable({
  providedIn: 'root'
})
export class SyncService {

  syncStatusChanged: EventEmitter<boolean>;

  /** Timestamp (ms) when the connection last dropped, or null while online. */
  private offlineSince: number | null = null;
  /** Minimum offline duration before a reconnect triggers recovery. */
  private static readonly RECOVERY_OFFLINE_DEBOUNCE_MS = 3000;

  constructor(
    private authService: AuthService,
    private pullDataService: PullDataService,
    private pushDataService: PushDataService,
    private securityService: SecurityService,
    private firestoreService: FirestoreService,
    private onlineOfflineService: OnlineOfflineService,
    private moduleActivationService: ModuleActivationService,
    private systemSettingsService: SystemSettingsService,
    private recoveryService: RecoveryService,
    // Injected so these services self-register in the EntityRegistry at app
    // start, making their entities discoverable by RecoveryService even before
    // the first cash movement / order is created.
    private cashTransactionService: CashTransactionService,
    private cashRegisterStatusLogReplicationService: CashRegisterStatusLogReplicationService,
    private salesOrderV3ReplicationService: SalesOrderV3ReplicationService,
    private salesOrderReplicationService: SalesOrderReplicationService,
  ) {
    this.syncStatusChanged = pullDataService.syncStatusChanged;

    // Sync data to indexeddb on login
    this.authService.userLoggedIn$.subscribe({
      next: () => {
        this.pullDataService.requestSync();
        this.pushDataService.pushLocalUpdatesToServer();
        // Generic recovery: restore replicated entities from the Desktop back into Firestore/IndexedDB.
        this.recoveryService.restoreAllReplicatedData();
      }
    });

    this.onlineOfflineService.connectionChanged.subscribe({
      next: () => {
        if (!this.onlineOfflineService.isOnline) {
          if (this.offlineSince === null) {
            this.offlineSince = Date.now();
          }
          return;
        }

        if (!this.authService.isAuthenticated()) {
          this.offlineSince = null;
          return;
        }

        const offlineDuration =
          this.offlineSince === null ? 0 : Date.now() - this.offlineSince;
        this.offlineSince = null;

        // Debounced: only run generic recovery after a real outage
        // (3+ seconds offline), not on every brief connectivity blip.
        if (offlineDuration >= SyncService.RECOVERY_OFFLINE_DEBOUNCE_MS) {
          this.recoveryService.restoreAllReplicatedData();
        }
      }
    });

    // Schedule sync to run every X minutes
    if (environment.production) {
      setInterval(() => {
        this.pullDataService.requestPeriodicSync();
        this.pushDataService.pushLocalUpdatesToServer();
      }, AppSettingsService.appSettings.offlineMode.syncIntervalMs);
    }

    if (`serviceWorker` in navigator) {
      navigator.serviceWorker.addEventListener('message', async event => this.authHeadersEventHandler(event));
    }
  }

  pushLocalUpdatesToServer() {
    this.pushDataService.pushLocalUpdatesToServer();
  }

  private async authHeadersEventHandler(event: any) {
    if (event.data.message === 'requestingAuthHeaders') {
      if (!this.authService.isAuthenticated()) {
        return;
      }

      const isFireStoreLoggedIn = await this.firestoreService.isLoggedIn();
      if (!isFireStoreLoggedIn) {
        await this.authService.renewFirebaseToken();
      }

      const firestoreIdToken = await this.firestoreService.getIdToken();
      const isPointOfSaleV3Enabled = await this.moduleActivationService.isPointOfSaleV3Enabled();
      const allowSellingBySerialNumber = await this.systemSettingsService.allowSellingBySerialNumber();
      const isDataReplicationEnabled = await this.systemSettingsService.isDataReplicationEnabled();
      const msg = {
        syncBatchId: Guid.create().toString(),
        accessToken: this.authService.accessToken,
        tenantId: this.authService.currentTenant,
        securityKey: this.securityService.key,
        firestoreIdToken: firestoreIdToken,
        userContext: {
          id: this.authService.currentUserId,
          email: this.authService.currentUserEmail,
          username: this.authService.currentUserEmail
        },
        environmentContext: {
          appVersion: environment.version,
          organizationId: this.authService.currentTenant,
          organizationName: this.authService.currentOrganization?.name
        },
        isPointOfSaleV3Enabled: isPointOfSaleV3Enabled,
        allowSellingBySerialNumber: allowSellingBySerialNumber,
        isDataReplicationEnabled: isDataReplicationEnabled
      };

      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          'command': 'authHeaders',
          'message': msg,
          'source': event.data.source
        });
      }
    }
  }

}
